import { DEFAULT_FORM_INPUT_TYPES, ITEM_STATUS_CODES, OPERATOR_RESPONSE_TYPES } from '../../utils/constants';
import {
    computeSummaryCounts,
    isReportUMCounterPromptLabel,
    resolveReportUMCounterValue,
} from '../../utils/reportUtils';
import { formatOperatorResponseLabel, getOperatorResponseChoices, OPERATOR_RESPONSE_VALUES } from '../../utils/formPromptMapping';
import type { tInspectionFormInput } from '../../types';

export type ReviewStatusTone = 'ok' | 'warn' | 'inop' | 'na';

export type ReviewRow = {
    label: string;
    detail: string;
    statusTone?: ReviewStatusTone;
    statusLabel?: string;
};

export type ReviewSection = {
    title: string;
    rows: ReviewRow[];
    isEmptyNotesSection?: boolean;
};

const STATUS_LABELS: Record<ReviewStatusTone, string> = {
    ok: 'OK',
    warn: 'Warning',
    inop: 'Inoperative',
    na: 'N/A',
};

function resolveStatusTone(value: string): ReviewStatusTone | undefined {
    switch (value) {
        case ITEM_STATUS_CODES.ok:
            return 'ok';
        case ITEM_STATUS_CODES.warn:
        case ITEM_STATUS_CODES.under_review:
        case ITEM_STATUS_CODES.status_unchanged:
            return 'warn';
        case ITEM_STATUS_CODES.inop:
            return 'inop';
        case ITEM_STATUS_CODES.na:
            return 'na';
        default:
            return undefined;
    }
}

function isStatusResponsePrompt(prompt: tInspectionFormInput): boolean {
    if (prompt.type !== DEFAULT_FORM_INPUT_TYPES.section) {
        return false;
    }

    const subType = prompt.subType;
    return (
        !subType ||
        subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
        subType === DEFAULT_FORM_INPUT_TYPES.yesNo
    );
}

export function getReviewRowDetail(prompt: tInspectionFormInput): string {
    if (prompt.subType === OPERATOR_RESPONSE_TYPES.NEW_ISSUES && prompt.value) {
        if (prompt.value === OPERATOR_RESPONSE_VALUES.noNewIssues) {
            return formatOperatorResponseLabel(prompt.value);
        }
        if (prompt.notes?.trim()) {
            return prompt.notes.trim();
        }
        return formatOperatorResponseLabel(prompt.value);
    }

    if (prompt.notes?.trim()) {
        return prompt.notes.trim();
    }

    if (prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox) {
        const selected = (prompt.selectedOptions ?? []).filter(
            (option: string) => typeof option === 'string' && option.trim().length > 0,
        );
        if (selected.length > 0) {
            return selected.join(' · ');
        }
    }

    if (prompt.subType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT && prompt.value) {
        return formatOperatorResponseLabel(prompt.value);
    }

    if (prompt.value && !isStatusResponsePrompt(prompt)) {
        return String(prompt.value);
    }

    if (prompt.description?.trim()) {
        return prompt.description.trim();
    }

    return '';
}

function buildReviewRow(prompt: tInspectionFormInput): ReviewRow {
    const row: ReviewRow = {
        label: prompt.label || 'Item',
        detail: getReviewRowDetail(prompt),
    };

    if (isStatusResponsePrompt(prompt) && prompt.value) {
        const statusTone = resolveStatusTone(prompt.value);
        if (statusTone) {
            row.statusTone = statusTone;
            row.statusLabel = STATUS_LABELS[statusTone];
        }
    } else if (prompt.subType === OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT && prompt.value) {
        row.statusLabel = formatOperatorResponseLabel(prompt.value);
    } else if (prompt.subType === OPERATOR_RESPONSE_TYPES.NEW_ISSUES && prompt.value) {
        if (prompt.value === ITEM_STATUS_CODES.warn) {
            row.statusTone = 'warn';
            row.statusLabel = 'Reported';
        } else {
            row.statusLabel = formatOperatorResponseLabel(prompt.value);
        }
    } else if (getOperatorResponseChoices(prompt.subType) && prompt.value) {
        row.statusLabel = formatOperatorResponseLabel(prompt.value);
        const statusTone = resolveStatusTone(prompt.value);
        if (statusTone) {
            row.statusTone = statusTone;
        }
    }

    return row;
}

export function buildReviewSections(prompts: tInspectionFormInput[]): ReviewSection[] {
    const sectionMap = new Map<string, ReviewRow[]>();
    const sectionOrder: string[] = [];

    const addToSection = (title: string, row: ReviewRow) => {
        if (!sectionMap.has(title)) {
            sectionMap.set(title, []);
            sectionOrder.push(title);
        }
        sectionMap.get(title)!.push(row);
    };

    for (const prompt of prompts) {
        if (isReportUMCounterPromptLabel(prompt.label)) {
            continue;
        }

        if (prompt.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
            const title = prompt.title || prompt.label || 'Additional Notes';
            addToSection(title, buildReviewRow(prompt));
            continue;
        }

        if (prompt.type === DEFAULT_FORM_INPUT_TYPES.section) {
            const title = prompt.title || 'Inspection';
            addToSection(title, buildReviewRow(prompt));
        }
    }

    return sectionOrder.map((title) => {
        const rows = sectionMap.get(title) ?? [];
        const isEmptyNotesSection =
            /additional notes?/i.test(title) && rows.every((row) => !row.detail && !row.statusTone);

        return {
            title,
            rows,
            isEmptyNotesSection,
        };
    });
}

export function computeReviewSummaryStats(prompts: tInspectionFormInput[]) {
    const { okCount, warnCount, inopCount } = computeSummaryCounts(prompts);
    let naCount = 0;

    for (const prompt of prompts) {
        if (isReportUMCounterPromptLabel(prompt.label)) {
            continue;
        }

        if (prompt.value === ITEM_STATUS_CODES.na) {
            naCount++;
        }
    }

    const totalItems = prompts.filter((prompt) => !isReportUMCounterPromptLabel(prompt.label)).length;
    const machineHours = resolveReportUMCounterValue(prompts);

    return {
        totalItems,
        okCount,
        warnCount,
        inopCount,
        naCount,
        machineHours,
    };
}

export function formatMachineHours(value: number | undefined): string {
    if (value == null || Number.isNaN(value)) {
        return '—';
    }

    return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    });
}
